import { RiErrorWarningLine, RiRefreshLine } from '@remixicon/react';
import type { ReactNode } from 'react';
import type { FallbackProps } from 'react-error-boundary';

import { Button } from '@playbook/atoms/button';

import { ApiError } from '@portal/pkg/api';
import { invalidateKeys } from '@portal/pkg/api/keys';
import { Notice } from '@portal/views/_shared/notice';

import css from './LoadFailed.module.css';

/**
 *	What a failed read offers instead of an empty page. Retrying is dropping
 *	the cached rejection — the hook below then starts a fresh request.
 */
export function LoadFailed({ error }: FallbackProps): ReactNode {
	// A refusal is not a failure, and retrying it will not help.
	if (error instanceof ApiError && error.status === 403) {
		return (
			<Notice variant="information" title="You cannot see these keys">
				This needs access to the organization&rsquo;s API keys. An administrator
				can grant it on your role.
			</Notice>
		);
	}

	return (
		<div className={css.failed}>
			<Notice variant="error" title="Could not load your keys">
				Something went wrong between here and the API.
			</Notice>
			<Button
				label="Try again"
				leadingIcon={<RiRefreshLine />}
				onClick={invalidateKeys}
			/>
		</div>
	);
}

export function LoadFailedInline({ what }: { what: string }): ReactNode {
	return (
		<p className={css.inline}>
			<RiErrorWarningLine aria-hidden="true" />
			{what}
		</p>
	);
}
